import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { useTrack } from 'hooks/analytics_events'
import { useGetPurchaseFulfillmentsByEventUuidsQuery } from 'shared/api/commerce/graphql_api'

import {
  getSessionSummary,
  getTransactionsUrl,
} from './meeting_package_fulfillment_utils'

/**
 * Fetches meeting package fulfillment for a group event and returns props
 * for ContactDetailRows.
 * @param {{ contactUuid?: string|null, contactId?: string|null }|null} [contactRef]
 *
 * @param {object} event
 */
export const useEventMeetingPackageContactRow = (event, contactRef = null) => {
  const history = useHistory()
  const track = useTrack()
  const { data: currentUser } = useGetUserQuery()
  const ownerUuid = currentUser?.uuid
  const isGroup = event?.eventType?.kind === 'group'
  const contactUuid = contactRef?.contactUuid || contactRef?.contactId || undefined
  const eventUuids = useMemo(() => (event?.uuid ? [event.uuid] : []), [event?.uuid])

  const { data } = useGetPurchaseFulfillmentsByEventUuidsQuery(
    { eventUuids, ownerUuid, contactUuid },
    { skip: !isGroup || eventUuids.length === 0 || !ownerUuid || !contactUuid }
  )

  return useMemo(() => {
    if (!isGroup || !contactUuid) {
      return { packageInfo: null, viewPackage: null }
    }

    const fulfillment = data?.purchaseFulfillmentsByEventUuids?.collection?.[0]
    const { purchasedPackage } = fulfillment || {}
    if (!fulfillment || !purchasedPackage) {
      return { packageInfo: null, viewPackage: null }
    }

    const sessionSummary = getSessionSummary(purchasedPackage.purchasedPackageItems)
    const transactionsUrl = getTransactionsUrl(purchasedPackage)
    const packageInfo = sessionSummary
      ? {
        fulfilled: sessionSummary.fulfilledUnits,
        total: sessionSummary.totalUnits,
        contactId: purchasedPackage?.contactUuid,
      } : null

    const hasRowContent = packageInfo || transactionsUrl
    if (!hasRowContent) {
      return { packageInfo: null, viewPackage: null }
    }

    const viewPackage = transactionsUrl
      ? () => {
        track({
          name: 'Meeting Interaction',
          properties: {
            action: 'view_meetings_package',
            location: 'meetings_page_right_rail',
          },
        })
        history.push(transactionsUrl)
      }
      : null

    return { packageInfo, viewPackage }
  }, [isGroup, contactUuid, data, history, track])
}
